import React, { useEffect, useRef, useState } from 'react';
import { getSiteSettings } from '../api/siteSettingsApi';

const COLORS = ['#ef4882', '#f5a623', '#0cad5e', '#2196f3', '#a261a7', '#ec529a', '#e53935', '#ffeb3b'];
const PIECES = 250;

function randomPiece(id, side, W, H) {
  const fromLeft = side === 'left';
  const angleDeg = 60 + Math.random() * 30; // 60°–90° — רובם כלפי מעלה
  const angleRad = (angleDeg * Math.PI) / 180;
  const speedV = 8 + Math.random() * 6;
  const spreadX = (Math.random() - 0.3) * W * 0.5; // פיזור לרוחב חצי מסך
  return {
    id,
    side,
    x: fromLeft ? Math.random() * 60 : W - Math.random() * 60,
    y: H + Math.random() * 30,
    vx: fromLeft ? spreadX / 80 : -spreadX / 80,
    vy: -speedV,
    gravity: 0.04 + Math.random() * 0.03,
    size: 6 + Math.random() * 10,
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
    rotation: Math.random() * 360,
    rotSpeed: (Math.random() - 0.5) * 6,
    shape: Math.random() > 0.5 ? 'rect' : 'circle',
    delay: Math.floor(Math.random() * 80),
  };
}

const FADE_DURATION = 2000; // ms לדהייה
const ACTIVE_DURATION = 8000; // ms לפני תחילת דהייה

const Confetti = () => {
  const [enabled, setEnabled] = useState(true);
  const [opacity, setOpacity] = useState(1);
  const [visible, setVisible] = useState(true);
  const canvasRef = useRef(null);
  const piecesRef = useRef([]);
  const rafRef = useRef(null);

  useEffect(() => {
    getSiteSettings().then(s => setEnabled(s.confetti !== false));
    const handler = (e) => setEnabled(e.detail?.confetti !== false);
    window.addEventListener('siteSettings:update', handler);
    return () => window.removeEventListener('siteSettings:update', handler);
  }, []);

  // טיימר: אחרי 10 שניות מתחיל דהייה, אחרי 12 שניות נעלם לגמרי
  useEffect(() => {
    if (!enabled) return;
    setOpacity(1);
    setVisible(true);
    const fadeTimer = setTimeout(() => {
      const start = performance.now();
      const fade = (now) => {
        const progress = Math.min((now - start) / FADE_DURATION, 1);
        setOpacity(1 - progress);
        if (progress < 1) {
          requestAnimationFrame(fade);
        } else {
          setVisible(false);
        }
      };
      requestAnimationFrame(fade);
    }, ACTIVE_DURATION);
    return () => clearTimeout(fadeTimer);
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !visible) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const W = () => canvas.width;
    const H = () => canvas.height;

    piecesRef.current = Array.from({ length: PIECES }, (_, i) =>
      randomPiece(i, i % 2 === 0 ? 'left' : 'right', W(), H())
    );

    const draw = () => {
      ctx.clearRect(0, 0, W(), H());

      piecesRef.current.forEach(p => {
        if (p.delay > 0) { p.delay--; return; }

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.fillStyle = p.color;
        if (p.shape === 'circle') {
          ctx.beginPath();
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
        }
        ctx.restore();

        p.x += p.vx;
        p.y += p.vy;
        p.vy += p.gravity;
        p.rotation += p.rotSpeed;

        if (p.y < -20 || p.x < -40 || p.x > W() + 40 || (p.vy > 0 && p.y > H() + 10)) {
          Object.assign(p, randomPiece(p.id, p.side, W(), H()));
        }
      });

      rafRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
    };
  }, [enabled, visible]);

  if (!enabled || !visible) return null;

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 9998,
        opacity,
        transition: 'none',
      }}
    />
  );
};

export default Confetti;
